import React, { useState, useEffect, useRef } from "react";
import { DocRenderer } from "react-doc-viewer";
import {
  CircularProgress,
  Typography,
  IconButton,
  Slider,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

interface SVGViewerState {
  scale: number;
  isDragging: boolean;
  startX: number;
  startY: number;
  translateX: number;
  translateY: number;
}

const SVGViewer: DocRenderer = ({ mainState: { currentDocument } }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [svgContent, setSvgContent] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewerState, setViewerState] = useState<SVGViewerState>({
    scale: 1,
    isDragging: false,
    startX: 0,
    startY: 0,
    translateX: 0,
    translateY: 0,
  });

  useEffect(() => {
    const fetchAndParseSVG = async () => {
      if (!currentDocument?.uri) {
        setError("No document provided.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(currentDocument.uri);
        if (!response.ok) throw new Error("Failed to fetch document.");

        const svgText = await response.text();

        // Basic SVG validation
        if (!svgText.includes("<svg")) {
          throw new Error("Invalid SVG file");
        }

        setSvgContent(svgText);
        setError(null);
      } catch (err) {
        setError("Error loading SVG file.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAndParseSVG();
  }, [currentDocument?.uri]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setViewerState((prev) => ({
      ...prev,
      isDragging: true,
      startX: e.clientX - prev.translateX,
      startY: e.clientY - prev.translateY,
    }));
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!viewerState.isDragging) return;

    setViewerState((prev) => ({
      ...prev,
      translateX: e.clientX - prev.startX,
      translateY: e.clientY - prev.startY,
    }));
  };

  const handleMouseUp = () => {
    setViewerState((prev) => ({
      ...prev,
      isDragging: false,
    }));
  };

  const handleZoom = (newScale: number) => {
    setViewerState((prev) => ({
      ...prev,
      scale: newScale,
    }));
  };

  const resetView = () => {
    setViewerState({
      scale: 1,
      isDragging: false,
      startX: 0,
      startY: 0,
      translateX: 0,
      translateY: 0,
    });
  };

  return (
    <div className="tw-p-4 tw-rounded-lg tw-bg-white tw-shadow-md tw-w-full">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <Typography variant="h6" className="tw-text-xl tw-font-bold">
          SVG Preview
        </Typography>

        <div className="tw-flex tw-items-center tw-gap-4">
          <IconButton
            onClick={() => handleZoom(Math.max(0.1, viewerState.scale - 0.1))}
          >
            <ZoomOutIcon />
          </IconButton>

          <div className="tw-w-32">
            <Slider
              value={viewerState.scale}
              min={0.1}
              max={3}
              step={0.1}
              onChange={(_, value) => handleZoom(value as number)}
              aria-label="Zoom"
            />
          </div>

          <IconButton
            onClick={() => handleZoom(Math.min(3, viewerState.scale + 0.1))}
          >
            <ZoomInIcon />
          </IconButton>

          <IconButton onClick={resetView}>
            <RestartAltIcon />
          </IconButton>
        </div>
      </div>

      {loading && (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-40">
          <CircularProgress />
        </div>
      )}

      {error && (
        <Typography className="tw-text-red-500 tw-font-semibold tw-text-center">
          {error}
        </Typography>
      )}

      {!loading && !error && svgContent && (
        <div
          ref={containerRef}
          className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden tw-relative"
          style={{
            height: "600px",
            cursor: viewerState.isDragging ? "grabbing" : "grab",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            style={{
              transform: `translate(${viewerState.translateX}px, ${viewerState.translateY}px) scale(${viewerState.scale})`,
              transformOrigin: "center center",
              transition: viewerState.isDragging
                ? "none"
                : "transform 0.1s ease-out",
            }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        </div>
      )}

      {!loading && !error && !svgContent && (
        <Typography className="tw-text-gray-500 tw-text-center">
          No content available.
        </Typography>
      )}
    </div>
  );
};

SVGViewer.fileTypes = ["svg"];
SVGViewer.weight = 1;

export default SVGViewer;
